/**
 * @fileoverview RadioGroup widget — exactly-one-select with per-option PASS/FAIL.
 * @module inspection-renderer/fields
 *
 * @component RadioGroupField
 */
import { useId } from 'react';
import type { FieldOption } from '@firesquirrel/core-domain';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { getPassFailStatus } from '../violations/passFailState';

interface OptionWithPassFail extends FieldOption {
  passFail?: 'pass' | 'fail' | 'neutral';
}

export function RadioGroupField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { options?: OptionWithPassFail[]; orientation?: 'horizontal' | 'vertical' };
  const options = (resolved?.options as OptionWithPassFail[] | undefined) ?? extras.options ?? [];
  const name = useId();

  // Pass/fail is owned by `extras.hasPassFail` + `extras.failValues` — see
  // `passFailState.ts`. Violation code bindings are metadata only.
  const passFailStatus = getPassFailStatus(field, value, isNA);

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="radioGroup"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      passFailStatus={passFailStatus}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <fieldset role="radiogroup" aria-required={!!(field.required || resolved?.required)}>
        <legend className="sr-only">{field.label}</legend>
        <div className={extras.orientation === 'horizontal' ? 'flex gap-4' : 'flex flex-col gap-2'}>
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={name}
                value={o.value}
                checked={value === o.value}
                onChange={() => onChange(o.value)}
                disabled={!!resolved?.disabled}
                data-passfail={o.passFail ?? 'neutral'}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </FieldWrapper>
  );
}
